import { useCallback, useEffect, useRef, useState } from "react";
import { type Sandbox, MAX_RECENT_SANDBOXES, SANDBOXES_ENDPOINT } from "./consts";

export const RECENT_SANDBOXES_KEY = "remocal:recent-routing-keys";

const readRecentKeys = (): ReadonlyArray<string> => {
  try {
    const item = window.localStorage.getItem(RECENT_SANDBOXES_KEY);
    return item !== null ? (JSON.parse(item) as ReadonlyArray<string>) : [];
  } catch {
    return [];
  }
};

export const useRecentSandboxKeys = (): ReadonlyArray<string> => readRecentKeys();

export const useAddRecentSandbox = (): {
  addRecentSandbox: (routingKey: string) => void;
} => {
  const addRecentSandbox = useCallback((routingKey: string) => {
    const recentKeys = readRecentKeys();
    const nextKeys = [routingKey, ...recentKeys.filter((k) => k !== routingKey)].slice(
      0,
      MAX_RECENT_SANDBOXES,
    );
    try {
      window.localStorage.setItem(RECENT_SANDBOXES_KEY, JSON.stringify(nextKeys));
    } catch {
      // localStorage unavailable
    }
  }, []);

  return { addRecentSandbox };
};

export const useSandboxes = (): {
  sandboxes: ReadonlyArray<Sandbox> | undefined;
  sandboxesLoading: boolean;
  retrieveSandboxes: () => Promise<void>;
} => {
  const [sandboxes, setSandboxes] = useState<undefined | ReadonlyArray<Sandbox>>();
  const [sandboxesLoading, setSandboxesLoading] = useState(false);
  const loadingRef = useRef(false);

  const retrieveSandboxes = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setSandboxesLoading(true);
    try {
      const response = await fetch(SANDBOXES_ENDPOINT);
      const loadedSandboxes = (await response.json()) as ReadonlyArray<Sandbox>;
      setSandboxes(loadedSandboxes);
    } finally {
      setSandboxesLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    void retrieveSandboxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { sandboxes, sandboxesLoading, retrieveSandboxes };
};
